import React, { useState, useCallback, useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceArea, ReferenceDot,
} from 'recharts';
import * as XLSX from 'xlsx';
import { Card } from '../../components/ui/Card';

const API = 'https://api.glowytics.com/api/search-research';
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// generic example keywords — just UX hints, not brand-bound
const EXAMPLE_KEYWORDS = ['iphone', 'winter coat', 'air conditioner', 'school bag', 'sunscreen',
  'new year gift', 'coffee machine', 'boots', 'holiday', 'nike'];

const nf = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const pf = (n) => (n == null ? '—' : `${(n * 100).toFixed(0)}%`);
const signPf = (n) => (n == null ? '—' : `${n >= 0 ? '+' : ''}${(n * 100).toFixed(0)}%`);
const money = (n) => (n == null ? '—' : `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })} ₺`);

async function post(path, body) {
  const res = await fetch(`${API}/${path}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json();
}

const compColor = (c) => ({ LOW: 'text-green-600', MEDIUM: 'text-amber-600', HIGH: 'text-red-600' }[c] || 'text-gray-500');

function Kpi({ label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${accent || 'text-gray-900'}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

export default function High5SearchResearch() {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [overview, setOverview] = useState(null);
  const [searchedKw, setSearchedKw] = useState('');
  const [trends, setTrends] = useState(null);

  const [cities, setCities] = useState(null);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesErr, setCitiesErr] = useState('');

  const research = useCallback(async (kw) => {
    const k = (kw ?? keyword).trim();
    if (!k) return;
    setLoading(true); setErr(''); setOverview(null); setTrends(null);
    setCities(null); setCitiesErr(''); setSearchedKw(k);
    try {
      // overview first (fast, primary), then trends
      const ov = await post('overview', { keyword: k });
      setOverview(ov);
      const [tr] = await Promise.allSettled([
        post('trends', { keyword: k, months: 12 }),
      ]);
      if (tr.status === 'fulfilled') setTrends(tr.value);
    } catch (e) {
      setErr(e.message || 'Error');
    } finally {
      setLoading(false);
    }
  }, [keyword]);

  const loadCities = useCallback(async () => {
    const k = (searchedKw || keyword).trim();
    if (!k) return;
    setCitiesLoading(true); setCitiesErr(''); setCities(null);
    try {
      const r = await post('cities', { keyword: k });
      setCities(r);
    } catch (e) {
      setCitiesErr(e.message || 'Could not load city breakdown');
    } finally {
      setCitiesLoading(false);
    }
  }, [keyword, searchedKw]);

  // chart data builders
  const monthlyChart = overview?.monthly?.map(r => ({
    label: `${MONTHS_EN[r.month - 1]} ${String(r.year).slice(2)}`,
    v: Number(r.monthly_searches || 0),
  })) || [];
  const yearChart = overview?.yearly?.map(y => ({ label: String(y.year), v: y.total })) || [];
  const trendChart = trends?.overTime?.map(p => ({ label: p.date, v: p.value })) || [];
  const lastTwoStart = trendChart.length >= 2 ? trendChart[trendChart.length - 2].label : null;
  const lastTwoEnd = trendChart.length ? trendChart[trendChart.length - 1].label : null;
  const cityList = cities?.cities?.filter(c => !c.error) || [];
  const cityChart = cityList.slice(0, 25).map(c => ({ label: c.province, v: c.avg_monthly }));

  // months-as-columns for the city table: union of all {year,month} the API returned, chronological
  const cityMonths = useMemo(() => {
    const set = new Map();
    for (const c of (cities?.cities || [])) {
      for (const m of (c.monthly || [])) {
        if (m && m.year && m.month) set.set(`${m.year}-${m.month}`, { year: m.year, month: m.month });
      }
    }
    return [...set.values()].sort((a, b) => (a.year - b.year) || (a.month - b.month));
  }, [cities]);
  // per-province lookup: "year-month" -> absolute searches
  const cityMonthMap = useMemo(() => {
    const out = {};
    for (const c of (cities?.cities || [])) {
      const mm = {};
      for (const m of (c.monthly || [])) mm[`${m.year}-${m.month}`] = Number(m.monthly_searches || 0);
      out[c.province] = mm;
    }
    return out;
  }, [cities]);

  // Excel export: all provinces × month columns (+ avg / total / competition)
  const exportCitiesXlsx = useCallback(() => {
    if (!cityList.length) return;
    const header = ['#', 'Province',
      ...cityMonths.map(mo => `${MONTHS_EN[mo.month - 1]} ${mo.year}`),
      'Avg/mo', '12-mo total', 'Competition'];
    const body = cityList.map((c, i) => [
      i + 1,
      c.province,
      ...cityMonths.map(mo => cityMonthMap[c.province]?.[`${mo.year}-${mo.month}`] ?? null),
      c.avg_monthly ?? null,
      c.total_12mo ?? null,
      c.competition || '',
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    ws['!cols'] = [{ wch: 4 }, { wch: 16 }, ...cityMonths.map(() => ({ wch: 9 })),
      { wch: 9 }, { wch: 11 }, { wch: 11 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Provinces');
    const safeKw = (searchedKw || 'keyword').replace(/[^\p{L}\p{N}]+/gu, '-').toLowerCase();
    XLSX.writeFile(wb, `search-volume-cities_${safeKw}.xlsx`);
  }, [cityList, cityMonths, cityMonthMap, searchedKw]);

  // ---- "When did it spike?" analysis: detect peak + biggest MoM / YoY surges from monthly data ----
  const surge = useMemo(() => {
    const monthly = overview?.monthly || [];
    if (monthly.length < 2) return null;
    const pts = monthly.map(r => ({
      year: r.year, month: r.month,
      label: `${MONTHS_EN[r.month - 1]} ${r.year}`,
      short: `${MONTHS_EN[r.month - 1]} ${String(r.year).slice(2)}`,
      v: Number(r.monthly_searches || 0),
    }));
    // all-time peak month
    let peak = pts[0];
    for (const p of pts) if (p.v > peak.v) peak = p;
    // month-over-month jumps
    const mom = [];
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1].v, cur = pts[i].v;
      if (prev > 0 && cur > prev) mom.push({ ...pts[i], delta: cur - prev, pct: (cur - prev) / prev });
    }
    mom.sort((a, b) => b.pct - a.pct);
    // year-over-year (same month vs previous year) jumps
    const byKey = {};
    pts.forEach(p => { byKey[`${p.year}-${p.month}`] = p.v; });
    const yoy = [];
    pts.forEach(p => {
      const prev = byKey[`${p.year - 1}-${p.month}`];
      if (prev != null && prev > 0 && p.v > prev) yoy.push({ ...p, delta: p.v - prev, pct: (p.v - prev) / prev });
    });
    yoy.sort((a, b) => b.pct - a.pct);
    return { peak, mom: mom.slice(0, 5), yoy: yoy.slice(0, 5) };
  }, [overview]);

  // biggest weekly jump in Google Trends (finer-grained "when")
  const trendSurge = useMemo(() => {
    if (trendChart.length < 2) return null;
    let best = null;
    for (let i = 1; i < trendChart.length; i++) {
      const d = trendChart[i].v - trendChart[i - 1].v;
      if (d > 0 && (!best || d > best.delta)) best = { date: trendChart[i].label, delta: d, value: trendChart[i].v };
    }
    return best;
  }, [trends]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Search Volume Radar</h1>
        <p className="mt-1 text-sm text-gray-500">
          Type a single keyword to see <b>when</b> (which month/period its searches rose) and <b>where</b>
          (which provinces), powered by live Google Ads Keyword Planner + Google Trends data. TR · brand-agnostic.
        </p>
      </div>

      {/* search bar */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && research()}
            placeholder="Type a keyword (e.g. winter coat, air conditioner, iphone)…"
            className="flex-1 min-w-[240px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
          />
          <button
            onClick={() => research()}
            disabled={loading}
            className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Researching…' : 'Research'}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className="self-center text-xs text-gray-400">examples:</span>
          {EXAMPLE_KEYWORDS.map(b => (
            <button key={b}
              onClick={() => { setKeyword(b); research(b); }}
              className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-600 hover:border-indigo-300 hover:bg-indigo-50">
              {b}
            </button>
          ))}
        </div>
        {err && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      </Card>

      {/* "When did it spike?" — primary insight panel */}
      {surge && (
        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">
            📈 When did it spike? <span className="font-normal text-gray-400">— "{searchedKw}"</span>
          </h3>
          <div className="grid gap-4 md:grid-cols-3">
            {/* peak */}
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
              <div className="text-xs font-medium text-indigo-500">Peak period (highest)</div>
              <div className="mt-1 text-xl font-bold text-indigo-700">{surge.peak.label}</div>
              <div className="mt-0.5 text-sm text-indigo-600/80">{nf(surge.peak.v)} searches / mo</div>
            </div>
            {/* MoM surges */}
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="mb-2 text-xs font-medium text-gray-500">Sharpest monthly jumps (vs prior month)</div>
              {surge.mom.length ? (
                <ul className="space-y-1">
                  {surge.mom.map((m, i) => (
                    <li key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{m.label}</span>
                      <span className="font-semibold text-green-600">{signPf(m.pct)}</span>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-xs text-gray-400">No notable monthly increase.</div>}
            </div>
            {/* YoY surges */}
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="mb-2 text-xs font-medium text-gray-500">Biggest YoY-growing months</div>
              {surge.yoy.length ? (
                <ul className="space-y-1">
                  {surge.yoy.map((m, i) => (
                    <li key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{m.label}</span>
                      <span className="font-semibold text-green-600">{signPf(m.pct)}</span>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-xs text-gray-400">No prior-year data to compare.</div>}
            </div>
          </div>
          {trendSurge && (
            <div className="mt-3 rounded-lg bg-pink-50 px-3 py-2 text-xs text-pink-700">
              Sharpest weekly jump in Google Trends: <b>{trendSurge.date}</b> (relative +{trendSurge.delta} pts, {trendSurge.value}/100)
            </div>
          )}
        </Card>
      )}

      {/* KPI cards */}
      {overview && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
          <Kpi label="Average / mo" value={nf(overview.avgMonthly)} sub="last 12 mo" />
          <Kpi label="Last 1 mo" value={nf(overview.last1mo)} />
          <Kpi label="Last 3 mo" value={nf(overview.last3mo)} />
          <Kpi label="Last 12 mo" value={nf(overview.last12mo)} />
          <Kpi label="YoY" value={pf(overview.yoy)} accent={overview.yoy >= 0 ? 'text-green-600' : 'text-red-600'} />
          <Kpi label="Competition" value={overview.competition || '—'} sub={overview.competitionIndex != null ? `idx ${overview.competitionIndex}` : ''} accent={compColor(overview.competition)} />
          <Kpi label="CPC (top of page)" value={overview.cpcLow != null ? money(overview.cpcLow) : '—'} sub={overview.cpcHigh != null ? `– ${money(overview.cpcHigh)}` : ''} />
        </div>
      )}

      {/* monthly */}
      {overview && (
        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">Monthly search volume (last 48 mo) — absolute <span className="font-normal text-gray-400">· peak marked</span></h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={monthlyChart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={3} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={nf} width={50} />
              <Tooltip formatter={(v) => nf(v)} />
              <Line type="monotone" dataKey="v" stroke="#4f46e5" strokeWidth={2} dot={false} />
              {surge && <ReferenceDot x={surge.peak.short} y={surge.peak.v} r={5} fill="#4f46e5" stroke="#fff" strokeWidth={2} />}
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* yearly */}
      {overview && yearChart.length > 0 && (
        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">Yearly total</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={yearChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} width={60} />
              <Tooltip formatter={(v) => nf(v)} />
              <Bar dataKey="v" fill="#6366f1" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Google Trends panel */}
      {trends && (
        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Google Trends — weekly relative interest (0–100)</h3>
            <span className="text-xs text-gray-400">last 2 weeks highlighted · sub-monthly signal</span>
          </div>
          {trendChart.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={Math.ceil(trendChart.length / 10)} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={30} />
                <Tooltip />
                {lastTwoStart && <ReferenceArea x1={lastTwoStart} x2={lastTwoEnd} fill="#fde68a" fillOpacity={0.5} />}
                <Line type="monotone" dataKey="v" stroke="#db2777" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-gray-400">Weekly trend unavailable (temporary Google block). Try again.</div>}

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="mb-2 text-xs font-semibold text-gray-600">Where interest is strongest — regional relative interest (Trends)</h4>
              {trends.byRegion?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {trends.byRegion.slice(0, 20).map(r => (
                    <span key={r.geo} className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700">
                      {r.geo} <b className="text-pink-600">{r.value}</b>
                    </span>
                  ))}
                </div>
              ) : <div className="text-xs text-gray-400">—</div>}
            </div>
            <div>
              <h4 className="mb-2 text-xs font-semibold text-gray-600">Rising queries (Trends)</h4>
              {trends.related?.rising?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {trends.related.rising.slice(0, 15).map((r, i) => (
                    <span key={i} className="rounded-md bg-pink-50 px-2 py-1 text-xs text-pink-700">{r.query}</span>
                  ))}
                </div>
              ) : <div className="text-xs text-gray-400">{trends.errors?.related ? 'Temporary Google block — try again' : '—'}</div>}
            </div>
          </div>
        </Card>
      )}

      {/* City breakdown (81 il) — "nerede" */}
      {overview && (
        <Card className="p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Where it's searched — 81 provinces (month-by-month absolute volume)</h3>
              <p className="text-xs text-gray-400">Each province is a separate live request; takes ~1-2 min. Each month is its own column (scroll sideways).</p>
            </div>
            <div className="flex items-center gap-2">
              {cityList.length > 0 && (
                <button onClick={exportCitiesXlsx}
                  className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100">
                  ⬇ Excel (all provinces)
                </button>
              )}
              <button onClick={loadCities} disabled={citiesLoading}
                className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-50">
                {citiesLoading ? 'Fetching 81 provinces…' : 'Fetch city breakdown'}
              </button>
            </div>
          </div>
          {citiesErr && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{citiesErr}</div>}
          {cities && (
            <>
              <div className="mb-2 text-xs text-gray-400">{cities.resolved}/{cities.count} provinces returned data</div>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={cityChart} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip formatter={(v) => nf(v)} />
                  <Bar dataKey="v" fill="#0ea5e9" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 max-h-96 overflow-auto">
                <table className="w-full whitespace-nowrap text-sm">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr className="border-b text-left text-xs text-gray-500">
                      <th className="sticky left-0 z-20 bg-white py-1.5 pr-2">#</th>
                      <th className="sticky left-8 z-20 bg-white pr-3">Province</th>
                      {cityMonths.map(mo => (
                        <th key={`${mo.year}-${mo.month}`} className="px-2 text-right font-medium">
                          {MONTHS_EN[mo.month - 1]} {String(mo.year).slice(2)}
                        </th>
                      ))}
                      <th className="px-2 text-right">Avg/mo</th>
                      <th className="px-2 text-right">12-mo total</th>
                      <th className="px-2">Competition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cityList.map((c, i) => (
                      <tr key={c.province} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="sticky left-0 bg-white py-1.5 pr-2 text-gray-400">{i + 1}</td>
                        <td className="sticky left-8 bg-white pr-3 font-medium text-gray-800">{c.province}</td>
                        {cityMonths.map(mo => {
                          const v = cityMonthMap[c.province]?.[`${mo.year}-${mo.month}`];
                          return (
                            <td key={`${mo.year}-${mo.month}`} className={`px-2 text-right tabular-nums ${v ? 'text-gray-700' : 'text-gray-300'}`}>
                              {v == null ? '—' : nf(v)}
                            </td>
                          );
                        })}
                        <td className="px-2 text-right font-semibold text-gray-900">{nf(c.avg_monthly)}</td>
                        <td className="px-2 text-right text-gray-500">{nf(c.total_12mo)}</td>
                        <td className={`px-2 ${compColor(c.competition)}`}>{c.competition || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
